import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Badge,
  Center,
  Container,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core"
import { IconChevronDown, IconChevronUp, IconSearch, IconSelector } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { KpiRowSkeleton, PageHeaderSkeleton, TableSkeleton } from "../../../../components/PageSkeletons"
import { formatDate, formatDateTime, formatInteger, formatRelative } from "../../../../lib/format-date"
import { activityLevel, compareRows, distinctProducts, summarise } from "./helpers"
import type { IProps, ITenantUsageRow, TActivityLevel, TProductFilter, TSortDir, TSortKey } from "./interfaces"
import classes from "./styles.module.css"

function productLabel(product: string | null): string {
  if (product === "lexicanon") return "Lexicanon"
  if (product === "voxcanon") return "Voxcanon"
  return "—"
}

function toProductFilter(value: string): TProductFilter {
  return value === "lexicanon" || value === "voxcanon" ? value : "all"
}

function SummaryStat({ label, value }: { label: React.ReactNode; value: string }): React.JSX.Element {
  return (
    <div className={`${classes.stat} lex-surface-card`}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text className={classes.statValue}>{value}</Text>
    </div>
  )
}

interface ISortHeaderProps {
  label: React.ReactNode
  sortBy: TSortKey
  active: boolean
  dir: TSortDir
  numeric?: boolean
  onSort: (key: TSortKey) => void
}

function SortHeader({ label, sortBy, active, dir, numeric = false, onSort }: ISortHeaderProps): React.JSX.Element {
  const icon = !active ? (
    <IconSelector size={14} className={classes.sortIcon} aria-hidden />
  ) : dir === "asc" ? (
    <IconChevronUp size={14} className={classes.sortIcon} aria-hidden />
  ) : (
    <IconChevronDown size={14} className={classes.sortIcon} aria-hidden />
  )
  return (
    <Table.Th className={numeric ? classes.numericTh : undefined}>
      <UnstyledButton className={classes.sortButton} data-numeric={numeric} onClick={() => onSort(sortBy)}>
        <span>{label}</span>
        {icon}
      </UnstyledButton>
    </Table.Th>
  )
}

function RelativeCell({ iso, now }: { iso: string | null; now: number }): React.JSX.Element {
  const { i18n } = useLingui()
  if (!iso) {
    return (
      <Text size="sm" c="dimmed">
        <Trans>Never</Trans>
      </Text>
    )
  }
  return (
    <Tooltip label={formatDateTime(i18n, iso)} withArrow>
      <Text size="sm">{formatRelative(i18n, now - new Date(iso).getTime(), { dayGranular: true })}</Text>
    </Tooltip>
  )
}

function levelTitle(i18n: ReturnType<typeof useLingui>["i18n"], level: TActivityLevel): string {
  switch (level) {
    case "active":
      return i18n._(msg`Active in the last 30 days`)
    case "idle":
      return i18n._(msg`Idle (31–90 days)`)
    case "dormant":
      return i18n._(msg`Dormant (90+ days)`)
    default:
      return i18n._(msg`No activity yet`)
  }
}

function TenantUsageRow({
  row,
  now,
  showsProduct,
}: {
  row: ITenantUsageRow
  now: number
  showsProduct: boolean
}): React.JSX.Element {
  const { i18n } = useLingui()
  const level = activityLevel(row.lastActivityAt, now)
  const title = levelTitle(i18n, level)
  return (
    <Table.Tr>
      <Table.Td>
        <Tooltip label={title} withArrow>
          <span className={classes.dot} data-level={level} role="img" aria-label={title} />
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {row.name}
          </Text>
          {showsProduct && row.product ? (
            <Badge variant="default" size="sm" className={classes.productBadge}>
              {productLabel(row.product)}
            </Badge>
          ) : null}
        </Group>
        {row.slug ? (
          <Text size="xs" c="dimmed">
            {row.slug}
          </Text>
        ) : null}
      </Table.Td>
      <Table.Td className={classes.numericTd}>{formatInteger(i18n, row.userCount)}</Table.Td>
      <Table.Td className={classes.numericTd}>{formatInteger(i18n, row.activeUserCount)}</Table.Td>
      <Table.Td className={classes.numericTd}>{formatInteger(i18n, row.meetingCount)}</Table.Td>
      <Table.Td className={classes.numericTd}>{formatInteger(i18n, row.meetingsLast30d)}</Table.Td>
      <Table.Td>
        <RelativeCell iso={row.lastLoginAt} now={now} />
      </Table.Td>
      <Table.Td>
        <RelativeCell iso={row.lastMeetingAt} now={now} />
      </Table.Td>
      <Table.Td>
        <Text size="sm">{row.createdAt ? formatDate(i18n, row.createdAt) : "—"}</Text>
      </Table.Td>
    </Table.Tr>
  )
}

export const TenantUsageView = ({ rows, isLoading }: IProps): React.JSX.Element => {
  // 1. STATE
  const [search, setSearch] = useState<string>("")
  const [product, setProduct] = useState<TProductFilter>("all")
  const [sortKey, setSortKey] = useState<TSortKey>("lastActivityAt")
  const [sortDir, setSortDir] = useState<TSortDir>("desc")

  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  // 3. DERIVED STATE
  // Captured once on mount — calling Date.now() during render is impure; the
  // lazy useState initializer is the project's sanctioned pattern for "now".
  const [now] = useState<number>(() => Date.now())

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const filtered = rows.filter((r) => {
      if (product !== "all" && r.product !== product) return false
      if (needle === "") return true
      return r.name.toLowerCase().includes(needle) || (r.slug ?? "").toLowerCase().includes(needle)
    })
    return [...filtered].sort((a, b) => compareRows(a, b, sortKey, sortDir))
  }, [rows, search, product, sortKey, sortDir])

  const totals = useMemo(() => summarise(visibleRows, now), [visibleRows, now])
  // A single-product host (the admin portal) has nothing to filter or badge.
  const showsProduct = distinctProducts(rows) > 1

  // 4. CALLBACKS
  const handleSort = (key: TSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
      return
    }
    setSortKey(key)
    // Names read best A→Z; everything else most-interesting (highest / newest) first.
    setSortDir(key === "name" ? "asc" : "desc")
  }

  // 6. GUARD CLAUSES
  if (isLoading) {
    return (
      <Container size="xl" py="md" className={classes.page}>
        <Stack gap="lg">
          <PageHeaderSkeleton withActions={false} />
          <KpiRowSkeleton count={4} />
          <TableSkeleton cols={7} />
        </Stack>
      </Container>
    )
  }

  // 7. RENDER
  return (
    <Container size="xl" py="md" className={classes.page}>
      <Stack gap="lg">
        <div>
          <Title order={2}>
            <Trans>Tenant usage</Trans>
          </Title>
          <Text c="dimmed" size="sm">
            <Trans>Activity across all tenants — users, sign-ins and recordings. No billing data.</Trans>
          </Text>
        </div>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <SummaryStat label={<Trans>Tenants</Trans>} value={formatInteger(i18n, totals.tenantCount)} />
          <SummaryStat label={<Trans>Active (30d)</Trans>} value={formatInteger(i18n, totals.activeTenantCount)} />
          <SummaryStat label={<Trans>Users</Trans>} value={formatInteger(i18n, totals.userCount)} />
          <SummaryStat label={<Trans>Recordings</Trans>} value={formatInteger(i18n, totals.meetingCount)} />
        </SimpleGrid>

        <Group justify="space-between" wrap="wrap" gap="sm">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={i18n._(msg`Search tenants…`)}
            leftSection={<IconSearch size={16} aria-hidden />}
            className={classes.search}
            aria-label={i18n._(msg`Search tenants`)}
          />
          {showsProduct ? (
            <SegmentedControl
              value={product}
              onChange={(v) => setProduct(toProductFilter(v))}
              data={[
                { label: i18n._(msg`All`), value: "all" },
                { label: "Lexicanon", value: "lexicanon" },
                { label: "Voxcanon", value: "voxcanon" },
              ]}
            />
          ) : null}
        </Group>

        {visibleRows.length === 0 ? (
          <Center mih={160}>
            <Text c="dimmed">
              {rows.length === 0 ? <Trans>No tenants yet.</Trans> : <Trans>No tenants match your filters.</Trans>}
            </Text>
          </Center>
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <Table highlightOnHover verticalSpacing="sm" className={classes.table}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th aria-label={i18n._(msg`Activity`)} />
                  <SortHeader
                    label={<Trans>Tenant</Trans>}
                    sortBy="name"
                    active={sortKey === "name"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Users</Trans>}
                    sortBy="userCount"
                    active={sortKey === "userCount"}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Active now</Trans>}
                    sortBy="activeUserCount"
                    active={sortKey === "activeUserCount"}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Recordings</Trans>}
                    sortBy="meetingCount"
                    active={sortKey === "meetingCount"}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Last 30d</Trans>}
                    sortBy="meetingsLast30d"
                    active={sortKey === "meetingsLast30d"}
                    dir={sortDir}
                    numeric
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Last login</Trans>}
                    sortBy="lastLoginAt"
                    active={sortKey === "lastLoginAt"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Last recording</Trans>}
                    sortBy="lastMeetingAt"
                    active={sortKey === "lastMeetingAt"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={<Trans>Created</Trans>}
                    sortBy="createdAt"
                    active={sortKey === "createdAt"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visibleRows.map((r) => (
                  <TenantUsageRow key={r.tenantId} row={r} now={now} showsProduct={showsProduct} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Container>
  )
}
